import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { styled } from "@mui/material/styles";
import {
  Box,
  Typography,
  Container,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import Logo from "../components/Logo";
import SimpleFooter from "../components/SimpleFooter";

const StyledPageContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
});

const ContentWrapper = styled("div")(({ theme }) => ({
  flexGrow: 1,
  display: "flex",
  flexDirection: "column",
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(8),
}));

const faqData = [
  {
    category: "General",
    question: "What is Contactie?",
    answer:
      "See and Be Seen for What You're Worth. Wondering how to prove you're more than just another \"one\" with the same skills? Contactie is more than a profile it's your trust score. Consolidate your links, showcase credibility, collect feedback, and stand out professionally.",
  },
  {
    category: "General",
    question: "Who can see my profile?",
    answer:
      "Your Contactie profile is public and can be accessed by anyone who has your unique username, a direct link, or, if you choose, via our search engine. This allows you to easily share your information with your audience.",
  },
  {
    category: "General",
    question: "What kind of information can I add to my profile?",
    answer:
      "Based on your subscription, you can customize your profile with a profile picture, cover photo, bio, resume, and your country. You can also add unlimited social media and custom links, manage your contact details (like phone and email), and take advantage of our unique coupon rating system to showcase collected feedback.",
  },
  {
    category: "General",
    question: "What is the coupon rating system?",
    answer:
      "The coupon rating system is designed for students, freelancers, and businesses. You can create unique coupons for your services (or deals) that customers can reserve and use. After a service is completed, customers can use the coupon to leave detailed ratings on skills like quality, communication, and timeliness, helping you build a strong online reputation.",
  },
  {
    category: "General",
    question: "Can I see how many people are visiting my profile?",
    answer:
      "Based on your subscription, the platform provides a detailed analytics and tracking system. You can view statistics on profile views and link clicks, along with helpful insights.",
  },

  {
    category: "Account",
    question: "How do I create an account?",
    answer:
      'Simply click the "Sign Up" button on our homepage. You will be prompted to enter your name, username, gender, email address, and a password. After that, you will receive a verification email to activate your account and ensure it is secure.',
  },
  {
    category: "Account",
    question: "I forgot my password. What should I do?",
    answer:
      'On the login page, click the "Forgot Password?" link. Enter the email address associated with your account, and we will send you instructions to reset your password.',
  },
  {
    category: "Account",
    question: "Why do I need to verify my email address?",
    answer:
      "Email verification is a crucial security step. It confirms that you are the legitimate owner of the email address, helps protect your account from unauthorized access, and ensures you receive important notifications from us.",
  },
  {
    category: "Account",
    question: "What personal information does Contactie collect?",
    answer:
      "We collect information necessary to provide our service, including your email address, username, first and last names, and country. You may also choose to provide a profile picture, cover picture, bio, and contact information like your phone number. We take your privacy seriously, sensitive data are encrypted in our database.",
  },
  {
    category: "Account",
    question: "Can I make my contact information private?",
    answer:
      "No. Contactie profiles are designed to be public. All information you choose to share will always be accessible via your QR code or username-based link. You can opt to be invisible in our search engine, but anyone with a direct link to your profile will still be able to access the shared information.",
  },
  {
    category: "Account",
    question: "How do I stop receiving email notifications?",
    answer:
      "You can unsubscribe using the link provided in any email you receive from Contactie. Once unsubscribed, you will no longer receive marketing emails, but we will still send essential emails necessary to provide our services.",
  },

  {
    category: "Billing",
    question: "Is Contactie free to use?",
    answer:
      "Contactie offers a free tier with core features for building your profile and connecting with others. We also have premium subscription plans that unlock advanced features.",
  },
  {
    category: "Billing",
    question: "How do I pay for premium features?",
    answer:
      "The platform operates on a budget-based system. You can make deposits to add funds to your personal Contactie budget. This budget can then be used to purchase items or premium features available on the platform.",
  },
  {
    category: "Billing",
    question: "How do I manage my subscription?",
    answer:
      'You can manage your subscription through the "Navigation Options" menu (gear icon at the top), allowing you to upgrade or downgrade your plan as needed. Subscriptions will automatically expire at the end of the current term.',
  },
  {
    category: "Billing",
    question: "How can I track my spending and deposits?",
    answer:
      "All financial activities are tracked. You can view a history of your deposits and purchases within your account settings, which includes transaction details like the amount, date, and a reference.",
  },
  {
    category: "Billing",
    question: "What payment methods do you accept for deposits?",
    answer:
      "Our platform facilitates deposits into your user budget. For specific payment methods currently available, please navigate to the deposit section within your account.",
  },

  {
    category: "Technical",
    question: "What browsers are supported?",
    answer:
      "Our platform is optimized for the latest versions of modern browsers, including Google Chrome, Mozilla Firefox, Safari, and Microsoft Edge.",
  },
  {
    category: "Technical",
    question: "How does Contactie protect my personal data?",
    answer:
      "We use multiple layers of security to protect your data. Your password is securely hashed and cannot be viewed by anyone. Sensitive personal information, such as your first name, last name, and phone number, is encrypted before being stored in our database to protect your privacy.",
  },
  {
    category: "Technical",
    question:
      "I’m getting an error when trying to log in or register multiple times. Why is this happening?",
    answer:
      "To protect the platform and our users from spam and abuse, we use IP-based rate limiting on sensitive actions like logging in and registering. If you make too many attempts in a short period, you may be temporarily blocked. Please wait a few moments and try again.",
  },
  {
    category: "Technical",
    question: "What kind of data is tracked when someone visits my profile?",
    answer:
      "For analytics purposes, we anonymously track profile views and link clicks. This includes the timestamp, the visitor’s country (inferred from their IP address or user input), and their gender (if provided). This data helps provide you with insights into your audience but is handled with privacy in mind.",
  },
  {
    category: "Technical",
    question: "How can I report a bug or suggest a new feature?",
    answer:
      'We value your feedback. The platform includes a dedicated feedback system accessible via the "Navigation Options" menu (gear icon at the top). You can categorize your submission as an "issue," "suggestion," "compliment," or "question" to ensure it reaches the appropriate team.',
  },
  {
    category: "Technical",
    question: "Does the Contactie website use cookies?",
    answer:
      "Yes. We use secure cookies to manage your login session and keep you signed in as you navigate the platform. These cookies are protected and essential for your account to function properly, helping maintain a safe and reliable experience.",
  },
];

const categories = [...new Set(faqData.map((faq) => faq.category))];

export default function FAQPage() {
  const [selectedCategory, setSelectedCategory] = useState(categories[0]);
  const [expanded, setExpanded] = useState(false);

  const handleCategoryChange = (event, newCategory) => {
    if (newCategory !== null) {
      setSelectedCategory(newCategory);
      setExpanded(false);
    }
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const filteredFaqs = faqData.filter(
    (faq) => faq.category === selectedCategory
  );

  return (
    <>
      <Helmet>
        <title>FAQ | Contactie</title>
        <meta
          name="description"
          content="Find answers to frequently asked questions about Contactie account management, billing, and platform features."
        />
      </Helmet>

      <StyledPageContainer>
        <Box
          component="header"
          sx={{
            py: 3,
            px: 5,
            display: { xs: "none", md: "flex" },
          }}
        >
          <Logo sx={{ height: 30, width: "auto" }} />
        </Box>

        <ContentWrapper>
          <Container maxWidth="md">
            <Box
              sx={{
                mb: 5,
                display: { xs: "flex", md: "none" },
                justifyContent: "center",
              }}
            >
              <Logo sx={{ height: 35, width: "auto" }} />
            </Box>

            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography variant="h2" component="h1" sx={{ fontWeight: 700 }}>
                Frequently Asked Questions
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                Can't find the answer you're looking for? Feel free to contact
                our support team.
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center", mb: 5 }}>
              <ToggleButtonGroup
                value={selectedCategory}
                exclusive
                onChange={handleCategoryChange}
                aria-label="faq categories"
                sx={{ flexWrap: "wrap", justifyContent: "center" }}
              >
                {categories.map((category) => (
                  <ToggleButton
                    key={category}
                    value={category}
                    aria-label={`${category} questions`}
                    sx={{
                      borderRadius: "20px !important",
                      px: 3,
                      m: 0.5,
                      border: "1px solid rgba(0, 0, 0, 0.12) !important",
                    }}
                  >
                    {category}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            <div>
              {filteredFaqs.map((faq, index) => (
                <Accordion
                  key={index}
                  expanded={expanded === `panel${index}`}
                  onChange={handleAccordionChange(`panel${index}`)}
                  sx={{
                    boxShadow: "none",
                    "&:before": { display: "none" },
                    borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`panel${index}-content`}
                    id={`panel${index}-header`}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography color="text.secondary">{faq.answer}</Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
              <Box
                sx={{
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                }}
              />
            </div>
          </Container>
        </ContentWrapper>
        <SimpleFooter />
      </StyledPageContainer>
    </>
  );
}
